/**
 * API 配置
 *
 * 后端 API 地址和端点定义
 * 后端使用 MySQL 数据库
 */

// 基础 URL - 根据环境切换
const getBaseURL = (): string => {
  // 开发环境
  if (import.meta.env.DEV) {
    return import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';
  }
  // 生产环境
  return import.meta.env.VITE_API_BASE_URL || '/api/v1';
};

export const API_BASE_URL: string = getBaseURL();

/**
 * API 端点定义
 * 与后端 REST API 路由一一对应
 */
export const API_ENDPOINTS: {
  auth: {
    login: string;
    register: string;
    logout: string;
    refresh: string;
    me: string;
    profile: string;
  };
  users: {
    base: string;
    byId: (id: string) => string;
    trips: (id: string) => string;
    collections: (id: string) => string;
    followers: (id: string) => string;
    following: (id: string) => string;
  };
  trips: {
    base: string;
    byId: (id: string) => string;
    itinerary: (id: string) => string;
    optimize: (id: string) => string;
  };
  destinations: {
    base: string;
    byId: (id: string) => string;
    pois: (id: string) => string;
    search: string;
  };
  pois: {
    base: string;
    byId: (id: string) => string;
    nearby: string;
    search: string;
  };
  journals: {
    base: string;
    byId: (id: string) => string;
    aiSummary: (id: string) => string;
    share: (id: string) => string;
  };
  community: {
    feed: string;
    posts: string;
    postById: (id: string) => string;
    comments: (id: string) => string;
    likes: (id: string) => string;
    follow: (id: string) => string;
  };
  ai: {
    chat: string;
    generateTrip: string;
    chatStream: string;
    recommendations: string;
    photoAnalysis: string;
    budget: string;
  };
  commerce: {
    affiliates: string;
    tracking: string;
    hotels: string;
    flights: string;
    tickets: string;
  };
  finance: {
    expenses: string;
    statistics: string;
    budget: string;
  };
  notifications: {
    base: string;
    read: (id: string) => string;
  };
  storage: {
    upload: string;
    delete: string;
  };
} = {
  // 认证
  auth: {
    login: '/auth/login',
    register: '/auth/register',
    logout: '/auth/logout',
    refresh: '/auth/refresh',
    me: '/auth/me',
    profile: '/auth/profile',
  },

  // 用户
  users: {
    base: '/users',
    byId: (id: string) => '/users/' + id,
    trips: (id: string) => '/users/' + id + '/trips',
    collections: (id: string) => '/users/' + id + '/collections',
    followers: (id: string) => '/users/' + id + '/followers',
    following: (id: string) => '/users/' + id + '/following',
  },

  // 行程规划
  trips: {
    base: '/trips',
    byId: (id: string) => '/trips/' + id,
    itinerary: (id: string) => '/trips/' + id + '/itinerary',
    optimize: (id: string) => '/trips/' + id + '/optimize',
  },

  // 目的地
  destinations: {
    base: '/destinations',
    byId: (id: string) => '/destinations/' + id,
    pois: (id: string) => '/destinations/' + id + '/pois',
    search: '/destinations/search',
  },

  // 景点
  pois: {
    base: '/pois',
    byId: (id: string) => '/pois/' + id,
    nearby: '/pois/nearby',
    search: '/pois/search',
  },

  // 旅行日记/手账
  journals: {
    base: '/journals',
    byId: (id: string) => '/journals/' + id,
    aiSummary: (id: string) => '/journals/' + id + '/ai-summary',
    share: (id: string) => '/journals/' + id + '/share',
  },

  // 社区
  community: {
    feed: '/community/feed',
    posts: '/community/posts',
    postById: (id: string) => '/community/posts/' + id,
    comments: (id: string) => '/community/posts/' + id + '/comments',
    likes: (id: string) => '/community/posts/' + id + '/likes',
    follow: (id: string) => '/community/users/' + id + '/follow',
  },

  // AI Agent
  ai: {
    chat: '/ai/chat',
    generateTrip: '/ai/generate-trip',
    chatStream: '/ai/chat/stream',
    recommendations: '/ai/recommendations',
    photoAnalysis: '/ai/photo-analysis',
    budget: '/ai/budget',
  },

  // 联盟/商业化
  commerce: {
    affiliates: '/commerce/affiliates',
    tracking: '/commerce/tracking',
    hotels: '/commerce/hotels',
    flights: '/commerce/flights',
    tickets: '/commerce/tickets',
  },

  // 财务
  finance: {
    expenses: '/finance/expenses',
    statistics: '/finance/statistics',
    budget: '/finance/budget',
  },

  // 通知
  notifications: {
    base: '/notifications',
    read: (id: string) => '/notifications/' + id + '/read',
  },

  // 文件存储
  storage: {
    upload: '/storage/upload',
    delete: '/storage/delete',
  },
};

export default API_ENDPOINTS;
